import { useState } from 'react';

import { DEFAULT_GATE_DUTY, DEFAULT_INTENSITY, dutyCycle } from '../core/limits';
import { SUGGESTED_BANDS } from '../core/safety';
import type { Band } from '../core/types';
import { sanitizeName, validateName } from '../core/validator';
import { useConnection } from '../store/connection';
import { exportAll } from '../store/persist';
import { useStore } from '../store/store';
import { DEFAULT_DEVICE_CONFIG, LEGACY_V41_CONFIG } from '../transport/types';
import { Segmented, Slider, formatWhen } from './common';
import { useConfirm } from './confirm';

export function Settings() {
  const store = useStore();

  return (
    <>
      <div className="card">
        <h3 className="card-title">Appearance</h3>
        <Segmented
          value={store.theme}
          onChange={(v) => store.setTheme(v)}
          options={[
            { value: 'light' as const, label: 'Light' },
            { value: 'dark' as const, label: 'Dark' },
          ]}
        />
      </div>

      <StepDefaults />
      <AvoidBands />
      <DeviceSection />
      <NotesSection />
      <BluetoothLog />

      <div className="card">
        <h3 className="card-title">Your data</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Everything stays on this phone. Nothing is uploaded anywhere.
        </p>
        <button
          className="btn small"
          onClick={() => {
            const blob = new Blob([exportAll()], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `pemf-backup-${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
          }}
        >
          Download a backup
        </button>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Starting values for a new step
 * ------------------------------------------------------------------ */

function StepDefaults() {
  const store = useStore();
  const duty = store.profile.defaultGateDutyPct ?? DEFAULT_GATE_DUTY;
  const intensity = store.profile.defaultIntensityPct ?? DEFAULT_INTENSITY;
  const shape = dutyCycle(intensity, duty);

  return (
    <div className="card">
      <h3 className="card-title">New step defaults</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        Where a new step starts. Every step can still be changed individually
        afterwards — this only sets the opening value.
      </p>

      <Slider
        label="Pulse / rest"
        value={duty}
        min={1}
        max={100}
        step={1}
        unit="%"
        onChange={(v) => store.setProfileDefaults({ gateDutyPct: v })}
        hint={
          duty === 100
            ? 'Continuous — no rest between pulses.'
            : `A pulse, then ${((100 - duty) / duty).toFixed(1)}x as long with the coil off.`
        }
      />

      <Slider
        label="Intensity"
        value={intensity}
        min={0}
        max={100}
        step={5}
        unit="%"
        onChange={(v) => store.setProfileDefaults({ intensityPct: v })}
        hint={
          intensity === 100
            ? `Full pulse — the field is on ${shape.average}% of the time, set entirely by pulse/rest above.`
            : `Chopped to ${shape.carrier}% within each pulse · ${shape.average}% of the time overall`
        }
      />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Avoided frequencies
 * ------------------------------------------------------------------ */

function AvoidBands() {
  const store = useStore();
  const bands = store.profile.avoidBands;
  const [adding, setAdding] = useState(false);
  const [lo, setLo] = useState(9.5);
  const [hi, setHi] = useState(10.5);

  const add = (band: Band) => {
    store.setAvoidBands([...bands, band]);
    setAdding(false);
  };

  return (
    <div className="card">
      <h3 className="card-title">Frequencies to skip</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        Anything in these ranges gets flagged, and sweeps that pass through them are caught too. Use
        it for frequencies that don't agree with you — if 10 Hz bothers you but 9 and 11 are fine,
        add 9.5–10.5 and a sweep will step around it.
      </p>

      {bands.length === 0 ? (
        <p className="faint">Nothing set. Every frequency the device can produce is available.</p>
      ) : (
        <div className="stack">
          {bands.map((band, i) => (
            <div key={i} className="row">
              <strong className="mono grow">
                {band.lo}–{band.hi} Hz
              </strong>
              <button
                className="btn small danger"
                onClick={() => store.setAvoidBands(bands.filter((_, n) => n !== i))}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <div style={{ marginTop: 14 }}>
          <Slider label="From" value={lo} min={0.5} max={199} step={0.5} unit="Hz" onChange={setLo} />
          <Slider label="To" value={hi} min={1} max={200} step={0.5} unit="Hz" onChange={setHi} />
          <div className="row">
            <button
              className="btn small primary"
              disabled={hi <= lo}
              onClick={() => add({ lo, hi })}
            >
              Add
            </button>
            <button className="btn small" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="row wrap" style={{ marginTop: 12 }}>
          <button className="btn small" onClick={() => setAdding(true)}>
            + Add a range
          </button>
          {SUGGESTED_BANDS.filter(
            (s) => !bands.some((b) => b.lo === s.band.lo && b.hi === s.band.hi),
          ).map((s) => (
            <button key={s.label} className="btn small ghost" onClick={() => add(s.band)}>
              + {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Device
 * ------------------------------------------------------------------ */

function DeviceSection() {
  const conn = useConnection();
  const store = useStore();
  const confirm = useConfirm();
  const [saveName, setSaveName] = useState('');
  const [raw, setRaw] = useState('');
  const [showIds, setShowIds] = useState(false);

  const connected = conn.state === 'connected';
  const canSave = connected && saveName.length > 0 && validateName(saveName).length === 0;

  return (
    <div className="card">
      <h3 className="card-title">Device</h3>

      <div className="field">
        <div className="field-label" style={{ marginBottom: 5 }}>
          How to connect
        </div>
        <Segmented
          value={conn.transportId}
          onChange={(v) => conn.setTransport(v)}
          options={[
            { value: 'web-bluetooth' as const, label: 'Bluetooth' },
            { value: 'manual' as const, label: 'Copy & paste' },
          ]}
        />
        {conn.transportId === 'manual' ? (
          <div className="faint" style={{ marginTop: 6 }}>
            Builds the commands for you to paste into a Bluetooth terminal app. Works on any phone,
            including iPhones.
          </div>
        ) : null}
      </div>

      {connected ? (
        <>
          <hr className="divider" />
          <div className="field-label" style={{ marginBottom: 6 }}>
            Programs stored on the device
          </div>
          {conn.savedPrograms.length === 0 ? (
            <p className="faint">None yet.</p>
          ) : (
            <div className="stack">
              {conn.savedPrograms.map((name) => (
                <div className="row" key={name}>
                  <strong className="mono grow">{name}</strong>
                  <button className="btn small" onClick={() => void conn.loadFromDevice(name)}>
                    Load
                  </button>
                  <button
                    className="btn small danger"
                    onClick={async () => {
                      const ok = await confirm({
                        title: `Delete ${name} from the device?`,
                        body: 'Your copy in the library stays.',
                        confirmLabel: 'Delete',
                        danger: true,
                      });
                      if (ok) await conn.deleteFromDevice(name);
                    }}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="row" style={{ marginTop: 12 }}>
            <input
              type="text"
              placeholder="Name to save as"
              value={saveName}
              maxLength={12}
              onChange={(e) => setSaveName(sanitizeName(e.target.value))}
              aria-label="Name for device storage"
            />
            <button
              className="btn small primary"
              disabled={!canSave}
              onClick={async () => {
                await conn.saveToDevice(saveName);
                setSaveName('');
              }}
            >
              Save
            </button>
          </div>

          <hr className="divider" />
          <div className="field">
            <div className="field-label" style={{ marginBottom: 5 }}>
              Start on its own at power-up
            </div>
            <Segmented
              value={conn.autorun ? 'on' : 'off'}
              onChange={(v) => void conn.setAutorun(v === 'on')}
              options={[
                { value: 'off' as const, label: 'Off' },
                { value: 'on' as const, label: 'On' },
              ]}
            />
            <div className="faint" style={{ marginTop: 6 }}>
              Runs the last program every time the device is switched on, with no phone needed.
            </div>
          </div>

          <div className="row">
            <input
              type="text"
              placeholder="Send a command directly"
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              aria-label="Command"
            />
            <button
              className="btn small"
              disabled={!raw.trim()}
              onClick={() => {
                void conn.sendRaw(raw.trim().toUpperCase());
                setRaw('');
              }}
            >
              Send
            </button>
          </div>
        </>
      ) : null}

      {conn.deviceProgram && conn.deviceProgram.cycles.length > 0 ? (
        <div className="issue warning" style={{ marginTop: 12 }}>
          Read <strong className="mono">{conn.deviceProgram.name}</strong> off the device —{' '}
          {conn.deviceProgram.cycles.length} steps.
          <div className="row" style={{ marginTop: 8 }}>
            <button
              className="btn small primary"
              onClick={() => {
                const id = store.createProgram({
                  name: conn.deviceProgram!.name,
                  title: conn.deviceProgram!.name,
                  cycles: conn.deviceProgram!.cycles.map((c) => ({ ...c })),
                });
                store.select(id);
                conn.clearDeviceProgram();
              }}
            >
              Add to library
            </button>
            <button className="btn small" onClick={conn.clearDeviceProgram}>
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      <hr className="divider" />
      <button className="btn small ghost" style={{ padding: 0 }} onClick={() => setShowIds((v) => !v)}>
        {showIds ? 'Hide' : 'Show'} technical details
      </button>
      {showIds ? (
        <>
          <pre className="pre" style={{ marginTop: 10 }}>
            {`service  ${conn.deviceConfig.serviceUuid}
rx       ${conn.deviceConfig.rxUuid}
tx       ${conn.deviceConfig.txUuid}
name     ${conn.deviceConfig.namePrefix}`}
          </pre>
          <div className="row wrap" style={{ marginTop: 10 }}>
            <button
              className="btn small"
              onClick={() => conn.setDeviceConfig(DEFAULT_DEVICE_CONFIG)}
              disabled={conn.deviceConfig.serviceUuid === DEFAULT_DEVICE_CONFIG.serviceUuid}
            >
              Firmware 4.2 or newer
            </button>
            <button
              className="btn small"
              onClick={() => conn.setDeviceConfig(LEGACY_V41_CONFIG)}
              disabled={conn.deviceConfig.serviceUuid === LEGACY_V41_CONFIG.serviceUuid}
            >
              Firmware 4.1 or older
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Notes
 * ------------------------------------------------------------------ */

function NotesSection() {
  const store = useStore();
  const confirm = useConfirm();
  const [note, setNote] = useState('');

  const add = () => {
    if (!note.trim()) return;
    store.addNote(note.trim());
    setNote('');
  };

  return (
    <div className="card">
      <h3 className="card-title">Notes</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        Track how things went — what you ran, how you felt afterwards.
      </p>
      <div className="row">
        <input
          type="text"
          placeholder="Slept better after the 3 Hz one…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') add();
          }}
          aria-label="New note"
        />
        <button className="btn small" disabled={!note.trim()} onClick={add}>
          Add
        </button>
      </div>

      {store.log.length === 0 ? (
        <p className="faint" style={{ marginTop: 12, marginBottom: 0 }}>
          Nothing yet.
        </p>
      ) : (
        <div style={{ marginTop: 12 }}>
          {store.log.slice(0, 40).map((entry, i) => (
            <div className="log-entry" key={i}>
              <div className="row">
                <span className={`badge ${entry.kind === 'session' ? 'ok' : 'neutral'}`}>
                  {entry.kind === 'session' ? 'Ran' : 'Note'}
                </span>
                <span className="grow">
                  {entry.kind === 'session' ? entry.programName : entry.text}
                </span>
                <span className="faint">{formatWhen(entry.at)}</span>
              </div>
            </div>
          ))}
          <button
            className="btn small danger"
            style={{ marginTop: 12 }}
            onClick={async () => {
              const ok = await confirm({
                title: 'Clear all notes?',
                body: 'Session records and notes for this profile will be deleted.',
                confirmLabel: 'Clear',
                danger: true,
              });
              if (ok) store.clearLog();
            }}
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Bluetooth log — collapsed by default
 * ------------------------------------------------------------------ */

function BluetoothLog() {
  const conn = useConnection();
  const [open, setOpen] = useState(false);

  return (
    <div className="card">
      <div className="card-head">
        <h3 className="card-title grow">Bluetooth log</h3>
        <button className="btn small" onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide' : `Show (${conn.bleLog.length})`}
        </button>
      </div>
      {open ? (
        conn.bleLog.length === 0 ? (
          <p className="faint" style={{ margin: 0 }}>
            Nothing sent or received yet.
          </p>
        ) : (
          <>
            <pre className="pre" style={{ maxHeight: 280, overflowY: 'auto' }}>
              {conn.bleLog
                .map(
                  (e) =>
                    `${new Date(e.at).toLocaleTimeString()}  ${
                      e.dir === 'tx' ? '→' : e.dir === 'rx' ? '←' : e.dir === 'error' ? '✕' : 'ⓘ'
                    }  ${e.text}`,
                )
                .join('\n')}
            </pre>
            <button className="btn small" style={{ marginTop: 10 }} onClick={conn.clearBleLog}>
              Clear
            </button>
          </>
        )
      ) : null}
    </div>
  );
}
